import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { checkDeletionBlockers, deleteAccount } from '../../lib/api';
import { Button, Input, Spinner } from '../ui';

/**
 * DangerZoneTab - Account deletion settings
 *
 * Shows blockers if account cannot be deleted,
 * otherwise shows delete button with confirmation.
 */
export default function DangerZoneTab() {
  const { logout } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [blockers, setBlockers] = useState([]);
  const [canDelete, setCanDelete] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationText, setConfirmationText] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Check for blockers on mount
  useEffect(() => {
    async function checkBlockers() {
      try {
        const result = await checkDeletionBlockers();
        setCanDelete(result.canDelete);
        setBlockers(result.blockers || []);
      } catch (error) {
        console.error('Error checking deletion blockers:', error);
        toast.error('Failed to check account status');
      } finally {
        setLoading(false);
      }
    }

    checkBlockers();
  }, []);

  const handleDelete = async () => {
    if (confirmationText !== 'DELETE MY ACCOUNT') {
      toast.error('Please type "DELETE MY ACCOUNT" to confirm');
      return;
    }

    setDeleting(true);
    try {
      const result = await deleteAccount({ confirmationText });

      if (result.blocked) {
        // Blockers changed since we checked
        setBlockers(result.blockers);
        setCanDelete(false);
        setShowConfirmation(false);
        toast.error('Cannot delete account: ' + result.blockers[0]?.message);
        return;
      }

      toast.success('Account deleted successfully');
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Error deleting account:', error);
      toast.error('Failed to delete account. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }

  return (
    <div>
      <h2 className="font-display font-bold text-xl text-cream mb-2">
        Danger Zone
      </h2>
      <p className="text-cream/60 mb-6">
        Irreversible and destructive actions.
      </p>

      {/* Account Deletion Section */}
      <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-6">
        <h3 className="font-semibold text-red-400 mb-2">Delete Account</h3>
        <p className="text-cream/60 text-sm mb-4">
          Permanently delete your Raflr account and all associated data.
          This action cannot be undone.
        </p>

        {blockers.length > 0 ? (
          // Show blockers
          <div className="space-y-3">
            <p className="text-cream/80 text-sm font-medium">
              Your account cannot be deleted until the following are resolved:
            </p>
            <ul className="space-y-2">
              {blockers.map((blocker, index) => (
                <li
                  key={index}
                  className="flex items-start gap-2 text-sm text-cream/70"
                >
                  <BlockerIcon type={blocker.type} />
                  <span>{blocker.message}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : showConfirmation ? (
          // Show confirmation form
          <div className="space-y-4">
            <div className="bg-midnight rounded-lg p-4 border border-red-500/20">
              <p className="text-cream/80 text-sm mb-4">
                This will permanently delete:
              </p>
              <ul className="text-cream/60 text-sm space-y-1 mb-4">
                <li>• Your profile and account information</li>
                <li>• All your competition entries</li>
                <li>• Your messages</li>
                <li>• Reviews you've written</li>
              </ul>
              <p className="text-red-400 text-sm font-medium">
                This action is permanent and cannot be reversed.
              </p>
            </div>

            <div>
              <label className="block text-sm text-cream/70 mb-2">
                Type <span className="font-mono text-red-400">DELETE MY ACCOUNT</span> to confirm:
              </label>
              <Input
                value={confirmationText}
                onChange={(e) => setConfirmationText(e.target.value)}
                placeholder="DELETE MY ACCOUNT"
                className="font-mono"
                disabled={deleting}
              />
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowConfirmation(false);
                  setConfirmationText('');
                }}
                disabled={deleting}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleDelete}
                loading={deleting}
                disabled={confirmationText !== 'DELETE MY ACCOUNT' || deleting}
              >
                Delete My Account
              </Button>
            </div>
          </div>
        ) : (
          // Show delete button
          <Button
            variant="danger"
            onClick={() => setShowConfirmation(true)}
          >
            Delete Account
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * BlockerIcon - Icon for different blocker types
 */
function BlockerIcon({ type }) {
  const iconClass = 'w-5 h-5 text-red-400 flex-shrink-0';

  switch (type) {
    case 'active_raffle':
      return (
        <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
        </svg>
      );
    case 'pending_handover':
      return (
        <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
        </svg>
      );
    case 'open_dispute':
      return (
        <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      );
    default:
      return (
        <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
  }
}
